import React, { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Grid,
  useTheme,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  Switch,
  FormControlLabel,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Barcode from "react-barcode";
import QRCode from "react-qr-code";
import {
  Settings as SettingsIcon,
  Print as PrintIcon,
  FormatSize as FormatSizeIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Info as InfoIcon,
  Preview as PreviewIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Close as CloseIcon,
  QrCodeScanner as QrCodeScannerIcon,
  ColorLens as ColorLensIcon,
  Crop as CropIcon,
  Diamond as DiamondIcon,
  Stars as StarsIcon,
  Image,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { printHtml } from "../../utils/htmlToPrint";

// ─── PVC Card Sizes (in pixels for 300 DPI) ──────────────────────

const PVC_SIZES = {
  CR80: { width: 340, height: 480, label: "CR-80 (Standard)" },
  CR80_ALT: { width: 360, height: 490, label: "CR-80 (Alternate)" },
  SQUARE: { width: 340, height: 340, label: "Square" },
  LARGE: { width: 380, height: 520, label: "Large" },
};

// ─── Design 1: Premium Glassmorphism ──────────────────────────────

const GlassCardWrapper = styled(Box)(({ theme, cardColor, isLandscape, cardSize, cardScale }) => {
  const size = PVC_SIZES[cardSize] || PVC_SIZES.CR80;
  let width = isLandscape ? size.height : size.width;
  let height = isLandscape ? size.width : size.height;

  return {
    width,
    height,
    borderRadius: "16px",
    overflow: "hidden",
    position: "relative",
    fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
    background: `linear-gradient(135deg, ${alpha(cardColor, 0.15)}, ${alpha(cardColor, 0.05)})`,
    backdropFilter: "blur(20px)",
    border: `1px solid ${alpha(cardColor, 0.3)}`,
    boxShadow: `0 20px 60px ${alpha(cardColor, 0.2)}`,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    transform: `scale(${cardScale})`,
    transformOrigin: "center",
    // PVC Bleed Area
    "&::before": {
      content: '""',
      position: "absolute",
      top: -3,
      left: -3,
      right: -3,
      bottom: -3,
      border: "2px dashed rgba(255,0,0,0.15)",
      borderRadius: "18px",
      pointerEvents: "none",
      zIndex: 10,
      display: "none",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: -50,
      right: -50,
      width: 200,
      height: 200,
      background: `radial-gradient(circle, ${alpha(cardColor, 0.2)}, transparent)`,
      borderRadius: "50%",
      pointerEvents: "none",
    },
  };
});

const GlassHeader = styled(Box)(({ color }) => ({
  height: 'auto',
  background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.8)})`,
  position: "relative",
  overflow: "hidden",
  display: "flex",
  alignItems: "flex-start",
  padding: "6px",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    background: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
    opacity: 0.4,
  },
}));

const GlassPhotoFrame = styled(Image)(() => ({
  // position: "absolute",
  // top: 80,
  // left: "50%",
  // transform: "translateX(-50%)",
  width: 110,
  height: 110,
  borderRadius: 5,
  padding: 3,
  // background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.5)})`,
  boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
  zIndex: 3,
}));

const GlassInfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: "2px 0",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
  "&:last-child": { borderBottom: "none" },
}));

// ─── Design 2: Neon Edge Card ──────────────────────────────────────

const NeonCardWrapper = styled(Box)(({ theme, cardColor, isLandscape, cardSize, cardScale }) => {
  const size = PVC_SIZES[cardSize] || PVC_SIZES.CR80;
  let width = isLandscape ? size.height : size.width;
  let height = isLandscape ? size.width : size.height;

  return {
    width,
    height,
    borderRadius: "16px",
    overflow: "hidden",
    position: "relative",
    fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
    background: `linear-gradient(145deg, #0a0a0a, #1a1a2e)`,
    border: `2px solid ${cardColor}`,
    boxShadow: `0 0 30px ${alpha(cardColor, 0.2)}, inset 0 0 30px ${alpha(cardColor, 0.05)}`,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    transform: `scale(${cardScale})`,
    transformOrigin: "center",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 50% 0%, ${alpha(cardColor, 0.1)}, transparent 70%)`,
      pointerEvents: "none",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: -3,
      left: -3,
      right: -3,
      bottom: -3,
      border: "2px dashed rgba(255,0,0,0.1)",
      borderRadius: "18px",
      pointerEvents: "none",
      zIndex: 10,
      display: "none",
    },
  };
});

const NeonHeader = styled(Box)(({ color }) => ({
  height: 'auto',
  background: `linear-gradient(135deg, ${alpha(color, 0.3)}, ${alpha(color, 0.1)})`,
  borderBottom: `2px solid ${alpha(color, 0.3)}`,
  position: "relative",
  overflow: "hidden",
  display: "flex",
  alignItems: "flex-start",
  padding: "8px",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
    background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
    boxShadow: `0 0 20px ${color}`,
  },
}));

const NeonGlow = styled(Box)(({ color }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 300,
  height: 300,
  background: `radial-gradient(circle, ${alpha(color, 0.08)}, transparent 70%)`,
  borderRadius: "50%",
  pointerEvents: "none",
}));

const NeonPhotoFrame = styled(Image)(({ color }) => ({
  // position: "absolute",
  // top: 85,
  // left: "50%",
  // transform: "translateX(-50%)",
  width: 110,
  height: 110,
  borderRadius: 5,
  padding: 2,
  background: `conic-gradient(from 0deg, ${color}, ${alpha(color, 0.5)}, ${color}, ${alpha(color, 0.5)}, ${color})`,
  zIndex: 3,
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 2,
    borderRadius: "50%",
    background: "#1a1a2e",
    zIndex: -1,
  },
}));

const NeonInfoRow = styled(Box)(({ theme, color }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: "2px 0",
  borderBottom: `1px solid ${alpha(color, 0.08)}`,
  "&:last-child": { borderBottom: "none" },
  "& .MuiTypography-root": {
    color: "#fff",
  },
}));

// ─── Shared Components ──────────────────────────────────────────────

const ControlsPanel = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: 1.5,
  alignItems: "center",
  justifyContent: "center",
  maxWidth: "1200px",
  margin: "auto",
  mb: 3,
  p: 2,
  borderRadius: 16,
  background: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: "blur(20px)",
  "@media print": { display: "none" },
}));

// ─── Constants ──────────────────────────────────────────────────────

const FIELD_GROUPS = {
  personal: {
    label: "Personal Information",
    icon: <PersonIcon />,
    fields: [
      { key: "firstName", label: "First Name" },
      { key: "middleName", label: "Father/Husband" },
      { key: "lastName", label: "Last Name" },
      { key: "dateOfBirth", label: "Date of Birth" },
    ],
  },
  contact: {
    label: "Contact Details",
    icon: <InfoIcon />,
    fields: [
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "addressDTO", label: "Address" },
    ],
  },
  professional: {
    label: "Professional Details",
    icon: <WorkIcon />,
    fields: [
      { key: "employeeCode", label: "Employee Code" },
      { key: "specialization", label: "Specialization" },
      { key: "experience", label: "Experience" },
      { key: "qualification", label: "Qualification" },
      { key: "degree", label: "Degree" },
      { key: "branch", label: "Branch" },
    ],
  },
};

const DEFAULT_VISIBLE_FIELDS = {
  firstName: true,
  middleName: false,
  lastName: true,
  dateOfBirth: false,
  phone: true,
  email: false,
  addressDTO: true,
  employeeCode: true,
  specialization: true,
  experience: false,
  qualification: false,
  degree: false,
  branch: false,
};

// ─── Main Component ──────────────────────────────────────────────────

export default function DigitalIDCard({ data, school, colors = {} }) {
  const theme = useTheme();
  const items = Array.isArray(data) ? data : [];

  // ── State ──
  const [design, setDesign] = useState("glass");
  const [showSettings, setShowSettings] = useState(false);
  const [cardColor, setCardColor] = useState(colors?.default || theme.palette.primary.main);
  const [isLandscape, setIsLandscape] = useState(false);
  const [cardScale, setCardScale] = useState(1);
  const [cardSize, setCardSize] = useState("CR80");
  const [code, setCode] = useState("QRCode");
  const [showBarcode, setShowBarcode] = useState(true);
  const [showPhoto, setShowPhoto] = useState(true);
  const [showRole, setShowRole] = useState(true);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  // const [fontcolor, setFontcolor] = useState("#FFFFFF");
  const [fontSize, setFontSize] = useState(12);
  const [showWatermark, setShowWatermark] = useState(true);
  const [showBorder, setShowBorder] = useState(true);
  const [showGlow, setShowGlow] = useState(true);
  const [showBleed, setShowBleed] = useState(false);

  const [visibleFields, setVisibleFields] = useState(DEFAULT_VISIBLE_FIELDS);

  // ── Helpers ──
  const getCardColor = (emp) => colors[emp.id] || colors.default || cardColor;
  // const getFontColor = () => fontcolor || "#FFFFFF";

  const formatValue = (emp, key) => {
    const val = emp[key];
    if (val === null || val === undefined) return "N/A";
    if (["dateOfBirth", "joiningDate"].includes(key)) {
      return new Date(val).toLocaleDateString("en-GB");
    }
    if (key === "addressDTO") {
      const addr = val;
      return [addr?.addressLine1, addr?.city, addr?.pincode, addr?.state].filter(Boolean).join(", ");
    }
    return String(val);
  };

  const toggleField = (key) =>
    setVisibleFields((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleGroup = (groupKey, value) => {
    const newState = {};
    FIELD_GROUPS[groupKey].fields.forEach((f) => (newState[f.key] = value));
    setVisibleFields((prev) => ({ ...prev, ...newState }));
  };

  const isGroupVisible = (groupKey) =>
    FIELD_GROUPS[groupKey].fields.some((f) => visibleFields[f.key]);

  // ── Actions ──
  const toggleLandscape = () => setIsLandscape((v) => !v);

  const handlePrint = () => {
    if (selectedCard?.id) {
      printHtml(selectedCard?.id);
    } else {
      printHtml("creative-cards-container");
    }
    window.location.reload();
  };

  const handleDownloadPDF = async () => {
    const el = document.getElementById("creative-cards-container");
    if (!el) return;
    try {
      const canvas = await html2canvas(el, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#fff",
        logging: false,
        width: el.scrollWidth,
        height: el.scrollHeight,
      });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const w = 210;
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, "PNG", 0, 0, w, h);
      pdf.save("pvc-id-cards.pdf");
    } catch (err) {
      console.error("PDF error:", err);
    }
  };

  const handlePreview = (emp) => {
    setSelectedCard(emp);
    setPreviewDialog(true);
  };

  // ── Render Glass Design ──
  const renderGlassCard = (emp, index) => {
    const color = getCardColor(emp);
    const fullName = [emp.firstName, emp.lastName].filter(Boolean).join(" ");
    const role = emp.experience < 1 ? "Teacher" : `Senior ${emp.specialization || ""} Teacher`;

    const infoFields = [
      { key: "employeeCode", label: "ID" },
      { key: "middleName", label: "Father/Husband" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "specialization", label: "Specialization" },
      { key: "experience", label: "Experience" },
      { key: "qualification", label: "Qualification" },
      { key: "degree", label: "Degree" },
      { key: "dateOfBirth", label: "DOB" },
      { key: "addressDTO", label: "Address" },
      { key: "branch", label: "Branch" },
    ].filter((f) => visibleFields[f.key]);

    return (
      <GlassCardWrapper
        key={emp.id || index}
        cardColor={color}
        isLandscape={isLandscape}
        cardSize={cardSize}
        cardScale={cardScale}
        sx={{
          "&::before": {
            display: showBleed ? "block" : "none",
          },
        }}
      >
        <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 1 }}>
          <DiamondIcon sx={{ color: alpha(color, 0.15), fontSize: 30 }} />
        </Box>
        <Box sx={{ position: "absolute", bottom: 12, left: 12, zIndex: 1 }}>
          <StarsIcon sx={{ color: alpha(color, 0.12), fontSize: 24 }} />
        </Box>

        <GlassHeader color={color}>
          <Avatar
            src={school?.logoUrl}
            alt="School Logo"
            sx={{
              width: 50,
              height: 50,
              borderRadius: 2,
              border: "2px solid rgba(255,255,255,0.3)",
              bgcolor: "white",
              p: 0.5,
            }}
          />
          <Box sx={{ ml: 1.5, color: "white", textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: isLandscape ? "0.7rem" : "0.7rem" }}>
              {school?.schoolName || "School Name"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, fontSize: isLandscape ? "0.6rem" : "0.6rem" }}>
              {[school?.addressDTO?.addressLine1, school?.addressDTO?.city, school?.addressDTO?.pincode, school?.addressDTO?.state]
                .filter(Boolean)
                .join(", ")}
              {school?.contactPhone && ` | Tel: ${school.contactPhone}`}
            </Typography>
          </Box>
        </GlassHeader>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {showPhoto && (
            <GlassPhotoFrame>
              <Avatar
                src={emp.photo}
                alt={fullName}
                sx={{ width: "100%", height: "100%", border: "3px solid white" }}
              >
                {fullName?.charAt(0) || "S"}
              </Avatar>
            </GlassPhotoFrame>
          )}

          <Box sx={{ m: 1, ml: 1.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                fontSize: isLandscape ? `${fontSize + 1}px` : `${fontSize + 2}px`,
              }}
            >
              {fullName || "Employee Name"}
            </Typography>
            {showRole && (
              <Typography
                size="small"
                sx={{
                  mt: 0.3,
                  // backgroundColor: color,
                  // color: "white",
                  fontWeight: 600,
                  borderRadius: "10px",
                  fontSize: isLandscape ? `${fontSize - 2}px` : `${fontSize}px`,
                  height: 22,
                }}
              >{role}</Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ px: 2.5, position: "relative" }}>
          <Box sx={{ mt: 2 }}>
            {infoFields.map((field) => (
              <GlassInfoRow key={field.key}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ fontSize: isLandscape ? `${fontSize - 2}px` : `${fontSize}px`, color: "#555" }}
                >
                  {field.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: isLandscape ? `${fontSize - 2}px` : `${fontSize}px`, color: "#777", maxWidth: "65%" }} noWrap>
                  {formatValue(emp, field.key)}
                </Typography>
              </GlassInfoRow>
            ))}
          </Box>
        </Box>

        {showBarcode && (
          <Box sx={{ position: "absolute", bottom: 8, right: 10 }}>
            {code === "QRCode" && (
              <Box sx={{ bgcolor: "white", p: 0.3, borderRadius: 1.5, boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>
                <QRCode value={`${emp.id}#${emp.employeeCode}`} size={50} />
              </Box>
            )}
            {code === "Barcode" && (
              <Box sx={{ bgcolor: "white", p: 0.3, borderRadius: 1.5, boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>
                <Barcode width={1} height={40} value={`${emp.id}#${emp.employeeCode}`} displayValue={false} />
              </Box>
            )}
          </Box>
        )}

        {showWatermark && (
          <Box
            sx={{
              position: "absolute",
              bottom: 40,
              left: 10,
              opacity: 0.025,
              fontSize: 40,
              fontWeight: "bold",
              color,
              pointerEvents: "none",
              transform: "rotate(-15deg)",
            }}
          >
            {school?.schoolName?.charAt(0) || "S"}
          </Box>
        )}
      </GlassCardWrapper>
    );
  };

  // ── Render Neon Design ──
  const renderNeonCard = (emp, index) => {
    const color = getCardColor(emp);
    const fullName = [emp.firstName, emp.lastName].filter(Boolean).join(" ");
    const role = emp.experience < 1 ? "Teacher" : `Senior ${emp.specialization || ""} Teacher`;

    const infoFields = [
      { key: "employeeCode", label: "ID" },
      { key: "middleName", label: "Father/Husband" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "specialization", label: "Specialization" },
      { key: "experience", label: "Experience" },
      { key: "qualification", label: "Qualification" },
      { key: "degree", label: "Degree" },
      { key: "dateOfBirth", label: "DOB" },
      { key: "addressDTO", label: "Address" },
      { key: "branch", label: "Branch" },
    ].filter((f) => visibleFields[f.key]);

    return (
      <NeonCardWrapper
        key={emp.id || index}
        cardColor={color}
        isLandscape={isLandscape}
        cardSize={cardSize}
        cardScale={cardScale}
        sx={{
          "&::after": {
            display: showBleed ? "block" : "none",
          },
        }}
      >
        {showGlow && <NeonGlow color={color} />}

        <NeonHeader color={color}>
          <Avatar
            src={school?.logoUrl}
            alt="School Logo"
            sx={{
              width: 50,
              height: 50,
              borderRadius: 2,
              border: `2px solid ${color}`,
              bgcolor: "white",
              p: 0.5,
              boxShadow: `0 0 15px ${alpha(color, 0.2)}`,
            }}
          />
          <Box sx={{ ml: 1.5, color: "white", textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: isLandscape ? "0.7rem" : "0.7rem" }}>
              {school?.schoolName || "School Name"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, fontSize: isLandscape ? "0.6rem" : "0.6rem" }}>
              {[school?.addressDTO?.addressLine1, school?.addressDTO?.city, school?.addressDTO?.pincode, school?.addressDTO?.state]
                .filter(Boolean)
                .join(", ")}
              {school?.contactPhone && ` | Tel: ${school.contactPhone}`}
            </Typography>
          </Box>
        </NeonHeader>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {showPhoto && (
            <NeonPhotoFrame color={color}>
              <Avatar
                src={emp.photo}
                alt={fullName}
                sx={{ width: "100%", height: "100%", border: "2px solid #1a1a2e" }}
              >
                {fullName?.charAt(0) || "S"}
              </Avatar>
            </NeonPhotoFrame>
          )}

          <Box sx={{ m: 1, ml: 1.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                fontSize: isLandscape ? `${fontSize + 1}px` : `${fontSize + 2}px`,
                color: "white",
              }}
            >
              {fullName || "Employee Name"}
            </Typography>
            {showRole && (
              <Typography
                sx={{
                  mt: 0.3,
                  backgroundColor: alpha(color, 0.25),
                  color: "white",
                  fontWeight: 600,
                  borderRadius: "10px",
                  border: `1px solid ${alpha(color, 0.2)}`,
                  boxShadow: `0 0 15px ${alpha(color, 0.15)}`,
                  fontSize: isLandscape ? `${fontSize - 2}px` : `${fontSize}px`,
                  height: 22,
                }}
              >{role}</Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ px: 2.5, position: "relative" }}>
          <Box sx={{ mt: 2 }}>
            {infoFields.map((field) => (
              <NeonInfoRow key={field.key} color={color}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ fontSize: isLandscape ? `${fontSize - 2}px` : `${fontSize}px`, color: alpha(color, 0.8) }}
                >
                  {field.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: isLandscape ? `${fontSize - 2}px` : `${fontSize}px`, color: "#aaa", maxWidth: "65%" }} noWrap>
                  {formatValue(emp, field.key)}
                </Typography>
              </NeonInfoRow>
            ))}
          </Box>
        </Box>

        {showBarcode && (
          <Box sx={{ position: "absolute", bottom: 8, right: 10 }}>
            {code === "QRCode" && (
              <Box sx={{ bgcolor: "#1a1a2e", p: 0.3, borderRadius: 1.5, border: `1px solid ${alpha(color, 0.2)}`, boxShadow: `0 0 15px ${alpha(color, 0.15)}` }}>
                <QRCode value={`${emp.id}#${emp.employeeCode}`} size={50} bgColor="#1a1a2e" fgColor="#ffffff" />
              </Box>
            )}
            {code === "Barcode" && (
              <Box sx={{ bgcolor: "#1a1a2e", p: 0.3, borderRadius: 1.5, border: `1px solid ${alpha(color, 0.2)}`, boxShadow: `0 0 15px ${alpha(color, 0.15)}` }}>
                <Barcode width={1} height={40} value={`${emp.id}#${emp.employeeCode}`} displayValue={false} background="#1a1a2e" lineColor="#ffffff" />
              </Box>
            )}
          </Box>
        )}
      </NeonCardWrapper>
    );
  };

  // ── Main Render ──
  return (
    <Box id="creative-id-cards-root">
      <ControlsPanel elevation={2}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
            Design:
          </Typography>
          <Select
            value={design}
            onChange={(e) => setDesign(e.target.value)}
            size="small"
            sx={{ minWidth: 120, fontSize: "0.75rem" }}
          >
            <MenuItem value="glass">✨ Glassmorphism</MenuItem>
            <MenuItem value="neon">💜 Neon Edge</MenuItem>
          </Select>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
            <QrCodeScannerIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Code:
          </Typography>
          <Select value={code} onChange={(e) => setCode(e.target.value)} size="small" sx={{ minWidth: 100, fontSize: "0.75rem" }}>
            <MenuItem value="Barcode">Bar code</MenuItem>
            <MenuItem value="QRCode">QR Code</MenuItem>
          </Select>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
            <ColorLensIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Color:
          </Typography>
          <input
            type="color"
            value={cardColor}
            onChange={(e) => setCardColor(e.target.value)}
            style={{ width: 28, height: 28, padding: 0, border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
          />
        </Box>

        <Button
          size="small"
          variant={isLandscape ? "contained" : "outlined"}
          startIcon={<FormatSizeIcon />}
          onClick={toggleLandscape}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          {isLandscape ? "Landscape" : "Portrait"}
        </Button>

        <Tooltip title="Toggle Bleed Marks">
          <IconButton
            size="small"
            onClick={() => setShowBleed((v) => !v)}
            color={showBleed ? "primary" : "default"}
          >
            <CropIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Button
          size="small"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setShowSettings((v) => !v)}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          Customize
        </Button>

        <Tooltip title="Print Cards">
          <IconButton size="small" onClick={handlePrint}>
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download PDF">
          <IconButton size="small" onClick={handleDownloadPDF}>
            <PictureAsPdfIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Preview Card">
          <IconButton size="small" onClick={() => handlePreview(items[0])}>
            <PreviewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </ControlsPanel>

      <Collapse in={showSettings}>
        <Paper sx={{ maxWidth: "1200px", margin: "auto", mb: 3, p: 2.5, bgcolor: "#f8f9fa", borderRadius: 3, "@media print": { display: "none" } }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>PVC Card Settings</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Card Size</InputLabel>
                    <Select value={cardSize} onChange={(e) => setCardSize(e.target.value)} label="Card Size">
                      {Object.entries(PVC_SIZES).map(([key, size]) => (
                        <MenuItem key={key} value={key}>{size.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" display="block" gutterBottom>
                    Card Scale: {cardScale.toFixed(1)}x
                  </Typography>
                  <Slider
                    value={cardScale}
                    min={0.8}
                    max={1.2}
                    step={0.05}
                    onChange={(_, v) => setCardScale(v)}
                    size="small"
                    sx={{ width: "90%" }}
                    valueLabelDisplay="auto"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" display="block" gutterBottom>
                    Font Size: {fontSize}px
                  </Typography>
                  <Slider
                    value={fontSize}
                    min={8}
                    max={16}
                    step={0.5}
                    onChange={(_, v) => setFontSize(v)}
                    size="small"
                    sx={{ width: "90%" }}
                    valueLabelDisplay="auto"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={<Switch checked={showBorder} onChange={(e) => setShowBorder(e.target.checked)} size="small" />}
                    label={<Typography variant="caption">Show Border</Typography>}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={<Switch checked={showPhoto} onChange={(e) => setShowPhoto(e.target.checked)} size="small" />}
                    label={<Typography variant="caption">Show Photo</Typography>}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={<Switch checked={showRole} onChange={(e) => setShowRole(e.target.checked)} size="small" />}
                    label={<Typography variant="caption">Show Role</Typography>}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={<Switch checked={showWatermark} onChange={(e) => setShowWatermark(e.target.checked)} size="small" />}
                    label={<Typography variant="caption">Watermark</Typography>}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={<Switch checked={showBarcode} onChange={(e) => setShowBarcode(e.target.checked)} size="small" />}
                    label={<Typography variant="caption">Show QR/BarCode</Typography>}
                  />
                </Grid>
                {design === "neon" && (
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={<Switch checked={showGlow} onChange={(e) => setShowGlow(e.target.checked)} size="small" />}
                      label={<Typography variant="caption">Neon Glow</Typography>}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Field Visibility</Typography>
              {Object.entries(FIELD_GROUPS).map(([groupKey, group]) => (
                <Accordion key={groupKey} sx={{ "&:before": { display: "none" }, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {group.icon}
                      <Typography variant="caption" fontWeight={600}>{group.label}</Typography>
                      <Chip size="small" label={isGroupVisible(groupKey) ? "Visible" : "Hidden"} color={isGroupVisible(groupKey) ? "success" : "default"} variant="outlined" sx={{ ml: 1, height: 20, fontSize: "0.6rem" }} />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" justifyContent="flex-end" mb={1}>
                      <Button size="small" onClick={() => toggleGroup(groupKey, !group.fields.every((f) => visibleFields[f.key]))} sx={{ fontSize: "0.65rem" }}>
                        {group.fields.every((f) => visibleFields[f.key]) ? "Hide All" : "Show All"}
                      </Button>
                    </Box>
                    <FormGroup>
                      <Grid container spacing={0.5}>
                        {group.fields.map((field) => (
                          <Grid item xs={12} sm={6} md={4} key={field.key}>
                            <FormControlLabel
                              control={<Checkbox checked={visibleFields[field.key] || false} onChange={() => toggleField(field.key)} size="small" color="primary" />}
                              label={<Typography variant="caption">{field.label}</Typography>}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </FormGroup>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      <Box id="creative-cards-container">
        <Grid container spacing={3} justifyContent="center" mt={3}>
          {items.map((emp, i) => (
            <Grid item key={emp.id || i} xs={12} sm={6} md={4} lg={3} sx={{ display: "flex", justifyContent: "center" }}>
              {design === "glass" ? renderGlassCard(emp, i) : renderNeonCard(emp, i)}
            </Grid>
          ))}
        </Grid>
      </Box>

      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 2 } }}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Card Preview</Typography>
            <IconButton onClick={() => setPreviewDialog(false)} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 3 }} id={selectedCard?.id}>
          {selectedCard && (design === "glass" ? renderGlassCard(selectedCard) : renderNeonCard(selectedCard))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
          <Button variant="contained" onClick={handlePrint}><PrintIcon fontSize="small" sx={{ mr: 1 }} /> Print</Button>
        </DialogActions>
      </Dialog>

      {items.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8, px: 2, bgcolor: "#f8f9fa", borderRadius: 4 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>No ID Cards to Display</Typography>
          <Typography variant="body2" color="textSecondary">Please add employee data to generate ID cards.</Typography>
        </Box>
      )}
    </Box>
  );
}